import { useState, type ReactNode } from "react";
import { FactStamp } from "@/components/site/FactStamp";
import { ShieldCheck, Clock, FileCheck, RefreshCw } from "lucide-react";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { RelatedLinksRail } from "@/components/site/aeo/RelatedLinksRail";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { Button } from "@/components/site/Button";
import {
  classifyContactCtaEvent,
  isContactPath,
  trackInternalLink,
} from "@/lib/analytics";
import { CaseCard } from "@/components/site/case/CaseCard";
import { ContactForm } from "@/components/site/ContactForm";
import { CallbackBar } from "@/components/site/CallbackBar";
import { LeadMagnetForm } from "@/components/site/LeadMagnetForm";
import { Tldr } from "@/components/site/Tldr";
import { CitableStats, type CitableStat } from "@/components/site/CitableStats";
import { RelatedHubs, type RelatedHubsProps } from "@/components/site/landing/RelatedHubs";
import { StillsMarquee } from "@/components/site/StillsMarquee";
import { LandingVideoReel } from "@/components/site/landing/LandingVideoReel";
import { defaultLandingStills, defaultLandingVideos, type LandingVideo } from "@/content/landing/media";
import type { CaseDetail } from "@/content/cases";
import { caseItems } from "@/content/cases";

export interface LandingFAQ {
  q: string;
  a: string;
}

export interface LandingTrustItem {
  value: string;
  label: string;
}

export interface LandingPriceRow {
  format: string;
  scope: string;
  price: string;
  lead?: string;
}

export interface LandingProcessStep {
  label: string;
  body: string;
  duration?: string;
}

export interface LandingComparisonRow {
  /** Beslutskriterium, t.ex. "Pris per film", "Time-to-publish". */
  criterion: string;
  a: string;
  b: string;
  /** Vilken kolumn som vinner kriteriet (visuell markering). */
  winner?: "a" | "b" | "tie";
}

export interface LandingComparison {
  /** Kort svar / dom överst — det besökaren egentligen vill veta. */
  verdict: ReactNode;
  optionA: string;
  optionB: string;
  rows: LandingComparisonRow[];
  /** "När passar vad", risk och kostnad i tre korta kort. */
  guidance?: { when: string; risk: string; cost: string };
  cta?: { label: string; to: string };
  heading?: string;
}

export interface LandingObjection {
  /** Invändningen som kunden faktiskt tänker. */
  objection: string;
  /** Vårt svar — kort och konkret. */
  answer: ReactNode;
}

export interface LandingRiskReducer {
  icon?: typeof ShieldCheck;
  title: string;
  body: string;
}

/** Standardgarantier för tjänstesidor (riskreducerare före closing-CTA). */
const DEFAULT_RISK_REDUCERS: LandingRiskReducer[] = [
  {
    icon: FileCheck,
    title: "Fast pris innan start",
    body: "Ni får ett tydligt estimat innan vi rullar. Inga öppna timpriser och inga efterhandsöverraskningar.",
  },
  {
    icon: Clock,
    title: "Snabb leverans",
    body: "Första klippet inom 10–14 dagar med Pulse-abonnemang — ni ser leverans, inte planering.",
  },
  {
    icon: RefreshCw,
    title: "Revideringar ingår",
    body: "Klipp, grading och ljud justeras tills filmen sitter. Ingen halvfärdig leverans.",
  },
  {
    icon: ShieldCheck,
    title: "Materialet är ert",
    body: "Allt slutmaterial är ert att använda fritt — sajt, sociala kanaler, sälj och rekrytering.",
  },
];


export interface LandingProps {
  eyebrow: string;
  title: ReactNode;
  lede: ReactNode;
  tldr?: ReactNode;
  stats?: { items: CitableStat[]; path: string; datePublished?: string; about?: string; heading?: string };
  ctaPrimary?: { label: string; to: string };
  ctaSecondary?: { label: string; to: string };
  bullets?: { title: string; body: string }[];
  /** Valfritt innehåll som renderas direkt under hero (t.ex. PriceFinder). */
  afterHero?: ReactNode;
  /** Valfritt innehåll som renderas efter innehållssektionerna, före FAQ. */
  afterSections?: ReactNode;

  /** Trust bar — small metrics row directly under hero. */
  trust?: LandingTrustItem[];
  /** Price table shown between bullets and body sections. */
  pricing?: { heading?: string; intro?: string; rows: LandingPriceRow[]; footnote?: string };
  /** Process timeline — step-by-step workflow. */
  process?: { heading?: string; intro?: string; steps: LandingProcessStep[] };
  /** CRO-jämförelseblock: kort svar, tabell, när/risk/kostnad, CTA. */
  comparison?: LandingComparison;
  sections: { id: string; heading: string; body: ReactNode }[];
  /**
   * Mobil: visa bara de N första sektionerna, resten bakom en "Läs mer"-knapp.
   * Desktop visar alltid allt (SEO-texten finns kvar i DOM:en).
   */
  mobileCollapseAfter?: number;
  /** Invändningshantering — kundens tvekan → vårt svar. Renderas före closing-CTA. */
  objections?: { heading?: string; intro?: string; items: LandingObjection[] };
  /**
   * Riskreducerare — kompakt garantiband före closing-CTA. `true` = standard­garantier
   * för tjänstesidor, eller skicka en egen lista. Utelämna för inget band.
   */
  riskReducers?: boolean | { heading?: string; items: LandingRiskReducer[] };
  /**
   * Lead magnet för sidans kluster — renderas mellan riskreducerare och
   * closing-CTA. `slug` måste finnas i `LEAD_MAGNETS`.
   */
  leadMagnet?: {
    slug: string;
    title?: string;
    description?: string;
    ctaLabel?: string;
  };
  faq?: LandingFAQ[];
  caseFilter?: (c: CaseDetail) => boolean;
  caseLimit?: number;
  closing?: { title: string; body: string; primary: { label: string; to: string }; secondary?: { label: string; to: string } };
  trackingId: string;
  related?: RelatedHubsProps;
  /** Path key in `internalLinks` model — adds an auto-rendered RelatedLinksRail. */
  relatedLinksFor?: string;
  /**
   * Answer-id ur `answers.ts`. När satt renderas ett citerbart `<QuickAnswer>`
   * direkt under hero-leden med det vettade kortsvaret. Leden behåller då
   * `data-speakable` men släpper `data-quick-answer` så att sidan har exakt
   * ett `data-quick-answer`-element (krav för AEO/Speakable).
   */
  quickAnswerId?: string;
  /** Stillbilder i marquee. Skicka [] eller false för att stänga av. Default = delad pool. */
  stills?: string[] | false;
  /** YouTube-videoreel. Skicka [] eller false för att stänga av. Default = delad pool. */
  videos?: LandingVideo[] | false;
  /**
   * Ersätter standardkontaktformuläret längst ned. Skicka en egen komponent
   * (t.ex. `FotoQuoteForm`) eller `false` för att utelämna formuläret helt.
   */
  contactSlot?: ReactNode | false;
  /**
   * Omslagsbild bakom hero. Renderas som bakgrund med mörk gradient så att
   * rubrik och lede behåller kontrast. Utelämna för ren bakgrund.
   */
  heroImage?: { src: string; alt?: string };
}

export function LandingPage(props: LandingProps) {
  const [showAllSections, setShowAllSections] = useState(false);
  const cases = (props.caseFilter ? caseItems.filter(props.caseFilter) : caseItems).slice(
    0,
    props.caseLimit ?? 3,
  );
  const resolvedStills =
    props.stills === false ? [] : props.stills && props.stills.length > 0 ? props.stills : defaultLandingStills;
  const resolvedVideos =
    props.videos === false ? [] : props.videos && props.videos.length > 0 ? props.videos : defaultLandingVideos;

  // Skickar comparison_to_contact_click / industry_to_contact_click när
  // sidans CTA pekar mot en kontaktsida — annars inget (Button kan ha cta_click).
  const onContactCta = (to: string, label: string, location: string) => {
    if (typeof window === "undefined" || !isContactPath(to)) return;
    const ev = classifyContactCtaEvent(window.location.pathname);
    if (ev) trackInternalLink(ev, { location, label, to });
  };


  return (
    <article className={props.heroImage ? "" : "pt-28"}>
      {/* Hero */}
      {props.heroImage && (
        <div className="relative pt-28">
          <Section className="pb-14 pt-10">
            <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
             <div>
              <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">
                {props.eyebrow}
              </div>
              <h1 className="text-balance text-5xl font-semibold leading-[1.02] md:text-7xl">
                {props.title}
              </h1>
              <p
                className="lede mt-6 max-w-3xl text-pretty text-lg text-foreground/85 md:text-xl"
                {...(props.quickAnswerId ? {} : { "data-quick-answer": true })}
                data-speakable
              >
                {props.lede}
              </p>
              {props.quickAnswerId && (
                <QuickAnswer answerId={props.quickAnswerId} className="mt-6" />
              )}
              {(props.ctaPrimary || props.ctaSecondary) && (
                <div className="mt-8 flex flex-wrap gap-3">
                  {props.ctaPrimary && (
                    <Button
                      to={props.ctaPrimary.to}
                      variant="mint"
                      arrow
                      onClick={() =>
                        onContactCta(props.ctaPrimary!.to, props.ctaPrimary!.label, "landing_hero_cta")
                      }
                    >
                      {props.ctaPrimary.label}
                    </Button>
                  )}
                  {props.ctaSecondary && (
                    <Button to={props.ctaSecondary.to} variant="outline">
                      {props.ctaSecondary.label}
                    </Button>
                  )}
                </div>
              )}
             </div>
             <div className="relative">
               <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 shadow-2xl">
                 <img
                   src={props.heroImage.src}
                   alt={props.heroImage.alt?.trim() || `${props.eyebrow} — bild för Fenny`}
                   className="aspect-[4/5] w-full object-cover sm:aspect-[16/10] lg:aspect-[4/5]"
                   loading="eager"
                   fetchPriority="high"
                   decoding="async"
                 />
                 <div
                   className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent"
                   aria-hidden
                 />
               </div>
             </div>
            </div>
          </Section>
        </div>
      )}
      {!props.heroImage && (
      <Section className="pb-10">
        <div className="max-w-4xl">
          <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">
            {props.eyebrow}
          </div>
          <h1 className="text-balance text-5xl font-semibold leading-[1.02] md:text-7xl">
            {props.title}
          </h1>
          <p
            className="lede mt-6 max-w-3xl text-pretty text-lg text-foreground/85 md:text-xl"
            {...(props.quickAnswerId ? {} : { "data-quick-answer": true })}
            data-speakable
          >
            {props.lede}
          </p>
          {props.quickAnswerId && (
            <QuickAnswer answerId={props.quickAnswerId} className="mt-6" />
          )}
          {(props.ctaPrimary || props.ctaSecondary) && (
            <div className="mt-8 flex flex-wrap gap-3">
              {props.ctaPrimary && (
                <Button
                  to={props.ctaPrimary.to}
                  variant="mint"
                  arrow
                  onClick={() => onContactCta(props.ctaPrimary!.to, props.ctaPrimary!.label, "landing_hero_cta")}
                >
                  {props.ctaPrimary.label}
                </Button>
              )}

              {props.ctaSecondary && (
                <Button to={props.ctaSecondary.to} variant="outline">
                  {props.ctaSecondary.label}
                </Button>
              )}
            </div>
          )}
        </div>
      </Section>
      )}


      {/* Valfri slot direkt under hero (PriceFinder m.m.) */}
      {props.afterHero && (
        <Section className="py-8">{props.afterHero}</Section>
      )}

      {/* Trust bar (kundbevis ovan fold) */}
      {props.trust && props.trust.length > 0 && (
        <Section className="pt-2 pb-6">
          <div className="grid grid-cols-2 gap-4 rounded-2xl border border-white/10 bg-surface/40 p-5 md:grid-cols-4 md:gap-6 md:p-6">
            {props.trust.map((t) => (
              <div key={t.label} className="text-center">
                <div className="text-2xl font-semibold leading-none text-mint md:text-3xl">
                  {t.value}
                </div>
                <div className="mt-2 text-xs uppercase tracking-wider text-muted-foreground md:text-[11px]">
                  {t.label}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* TL;DR */}
      {props.tldr && (
        <Section className="py-6">
          <div className="mx-auto max-w-3xl">
            <Tldr>{props.tldr}</Tldr>
          </div>
        </Section>
      )}

      {/* CRO-jämförelseblock (kort svar → tabell → när/risk/kostnad → CTA) */}
      {props.comparison && props.comparison.rows.length > 0 && (
        <Section className="py-10" id="jamforelse">
          <div className="mx-auto max-w-4xl">
            <EyebrowHeading
              eyebrow="Kort svar"
              title={<>{props.comparison.heading ?? "Vilket ska ni välja?"}</>}
            />
            <div className="mt-5 rounded-2xl border border-mint/30 bg-mint/5 p-5 text-pretty text-base leading-relaxed text-foreground/90 md:p-6 md:text-lg">
              {props.comparison.verdict}
            </div>

            <div className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-surface/40">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-white/10 bg-surface/60 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 md:px-6">Kriterium</th>
                    <th className="px-4 py-3 md:px-6">{props.comparison.optionA}</th>
                    <th className="px-4 py-3 md:px-6">{props.comparison.optionB}</th>
                  </tr>
                </thead>
                <tbody>
                  {props.comparison.rows.map((r) => (
                    <tr key={r.criterion} className="border-b border-white/5 last:border-0 align-top">
                      <td className="px-4 py-4 font-medium text-foreground md:px-6">{r.criterion}</td>
                      <td
                        className={
                          "px-4 py-4 md:px-6 " +
                          (r.winner === "a"
                            ? "font-medium text-mint"
                            : "text-foreground/80")
                        }
                      >
                        {r.a}
                      </td>
                      <td
                        className={
                          "px-4 py-4 md:px-6 " +
                          (r.winner === "b"
                            ? "font-medium text-mint"
                            : "text-foreground/80")
                        }
                      >
                        {r.b}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {props.comparison.guidance && (
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                <div className="rounded-2xl border border-white/10 bg-surface/40 p-5">
                  <div className="mb-2 text-xs uppercase tracking-widest text-mint">När passar vad</div>
                  <p className="text-sm leading-relaxed text-foreground/85">{props.comparison.guidance.when}</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-surface/40 p-5">
                  <div className="mb-2 text-xs uppercase tracking-widest text-mint">Risk</div>
                  <p className="text-sm leading-relaxed text-foreground/85">{props.comparison.guidance.risk}</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-surface/40 p-5">
                  <div className="mb-2 text-xs uppercase tracking-widest text-mint">Kostnad</div>
                  <p className="text-sm leading-relaxed text-foreground/85">{props.comparison.guidance.cost}</p>
                </div>
              </div>
            )}

            {props.comparison.cta && (
              <div className="mt-8">
                <Button
                  to={props.comparison.cta.to}
                  variant="mint"
                  arrow
                  onClick={() => onContactCta(props.comparison!.cta!.to, props.comparison!.cta!.label, "landing_comparison_cta")}
                >
                  {props.comparison.cta.label}
                </Button>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Citable stats (AEO) */}
      {props.stats && props.stats.items.length > 0 && (
        <Section className="py-4">
          <div className="mx-auto max-w-4xl">
            <CitableStats
              stats={props.stats.items}
              path={props.stats.path}
              datePublished={props.stats.datePublished}
              about={props.stats.about}
              heading={props.stats.heading}
            />
          </div>
        </Section>
      )}

      {/* Value bullets */}
      {props.bullets && props.bullets.length > 0 && (
        <Section className="py-10">
          <div className="grid gap-6 md:grid-cols-3">
            {props.bullets.map((b) => (
              <div
                key={b.title}
                className="rounded-2xl border border-white/10 bg-surface/40 p-6"
              >
                <div className="mb-2 text-xs uppercase tracking-widest text-mint">
                  {b.title}
                </div>
                <p className="text-pretty text-base text-foreground/85">{b.body}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Video reel — kurerad mix från YouTube */}
      {resolvedVideos.length > 0 && (
        <LandingVideoReel videos={resolvedVideos} trackingId={props.trackingId} />
      )}



      {/* Pris-tabell */}
      {props.pricing && props.pricing.rows.length > 0 && (
        <Section className="py-10" id="priser">
          <div className="mx-auto max-w-4xl">
            <EyebrowHeading
              eyebrow="Priser"
              title={<>{props.pricing.heading ?? "Vad det kostar"}</>}
            />
            {props.pricing.intro && (
              <p className="mx-auto mt-4 max-w-2xl text-pretty text-base text-foreground/80">
                {props.pricing.intro}
              </p>
            )}
            <div className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-surface/40">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-white/10 bg-surface/60 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 md:px-6">Format</th>
                    <th className="hidden px-4 py-3 md:table-cell md:px-6">Typiskt scope</th>
                    <th className="px-4 py-3 text-right md:px-6">Pris</th>
                    <th className="hidden px-4 py-3 text-right md:table-cell md:px-6">Leverans</th>
                  </tr>
                </thead>
                <tbody>
                  {props.pricing.rows.map((r) => (
                    <tr key={r.format} className="border-b border-white/5 last:border-0">
                      <td className="px-4 py-4 align-top font-medium text-foreground md:px-6">
                        {r.format}
                        <div className="mt-1 text-xs text-muted-foreground md:hidden">{r.scope}</div>
                      </td>
                      <td className="hidden px-4 py-4 align-top text-foreground/80 md:table-cell md:px-6">{r.scope}</td>
                      <td className="px-4 py-4 text-right align-top font-mono text-mint md:px-6">{r.price}</td>
                      <td className="hidden px-4 py-4 text-right align-top text-foreground/70 md:table-cell md:px-6">{r.lead ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* En rikstäckande prislista: samma spann på varje ortssida, så att
                inget läses som motstridiga prisfakta mellan städer. */}
            <p className="mt-4 text-center text-xs text-muted-foreground">
              Samma prislista i hela Sverige — inga ortstillägg och inga resepåslag.
              Slutpriset styrs av omfattning, inte av var produktionen sker.
            </p>
            {props.pricing.footnote && (
              <p className="mt-2 text-center text-xs text-muted-foreground">{props.pricing.footnote}</p>
            )}
            {/* Daterad, attribuerad prisdata — det svarsmotorer kan citera. */}
            <FactStamp className="mt-6" />
          </div>
        </Section>
      )}

      {/* Process / tidslinje */}
      {props.process && props.process.steps.length > 0 && (
        <Section className="py-10" id="process">
          <div className="mx-auto max-w-4xl">
            <EyebrowHeading
              eyebrow="Process"
              title={<>{props.process.heading ?? "Så jobbar vi"}</>}
            />
            {props.process.intro && (
              <p className="mx-auto mt-4 max-w-2xl text-pretty text-base text-foreground/80">
                {props.process.intro}
              </p>
            )}
            <ol className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {props.process.steps.map((s, i) => (
                <li
                  key={s.label}
                  className="relative rounded-2xl border border-white/10 bg-surface/40 p-5"
                >
                  <div className="text-xs uppercase tracking-widest text-mint">
                    {String(i + 1).padStart(2, "0")}
                    {s.duration && <span className="ml-2 text-muted-foreground">· {s.duration}</span>}
                  </div>
                  <div className="mt-2 text-lg font-semibold text-foreground">{s.label}</div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/80">{s.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </Section>
      )}

      {/* Body sections */}
      <Section className="py-10">
        <div className="mx-auto max-w-3xl space-y-12">
          {props.sections.map((s, i) => {
            const collapsed =
              typeof props.mobileCollapseAfter === "number" &&
              i >= props.mobileCollapseAfter &&
              !showAllSections;
            return (
              <section
                key={s.id}
                id={s.id}
                className={"scroll-mt-28 " + (collapsed ? "hidden md:block" : "")}
              >
                <div className="mb-2 text-xs uppercase tracking-widest text-mint">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h2 className="text-balance text-3xl font-semibold leading-tight md:text-4xl">
                  {s.heading}
                </h2>
                <div className="mt-4 space-y-4 text-base leading-relaxed text-foreground/85 md:text-lg">
                  {s.body}
                </div>
              </section>
            );
          })}
          {typeof props.mobileCollapseAfter === "number" &&
            props.sections.length > props.mobileCollapseAfter &&
            !showAllSections && (
              <button
                type="button"
                onClick={() => setShowAllSections(true)}
                className="w-full rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-foreground/80 md:hidden"
              >
                Läs mer ({props.sections.length - props.mobileCollapseAfter} avsnitt till)
              </button>
            )}
        </div>
      </Section>


      {/* Cases */}
      {cases.length > 0 && (
        <Section className="py-10">
          <EyebrowHeading
            eyebrow="Utvalda case"
            title={<>Så här ser det ut hos kunder</>}
          />
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {cases.map((c) => (
              <CaseCard key={c.id} item={c} />
            ))}
          </div>
        </Section>
      )}

      {/* Stills marquee — bakom kamerorna */}
      {resolvedStills.length >= 3 && <StillsMarquee images={resolvedStills} />}

      {/* Valfri slot efter innehållssektionerna (t.ex. foto+film-showcase) */}
      {props.afterSections}

      {/* FAQ (visual — schema sätts i route head()) */}

      {props.faq && props.faq.length > 0 && (
        <Section className="py-10">
          <EyebrowHeading eyebrow="Vanliga frågor" title={<>Det vi får mest av</>} />
          <div className="mx-auto mt-10 max-w-3xl space-y-3">
            {props.faq.map((f) => (
              <details
                key={f.q}
                className="group rounded-2xl border border-white/10 bg-surface/40 p-5 open:bg-surface/60"
              >
                <summary className="cursor-pointer list-none text-base font-medium text-foreground md:text-lg">
                  {f.q}
                </summary>
                <p className="mt-3 text-pretty text-base leading-relaxed text-foreground/80">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </Section>
      )}

      {/* Related hubs — format-longtails, siblings, comparisons */}
      {props.related && <RelatedHubs {...props.related} />}

      {/* AEO/SEO internal links rail */}
      {props.relatedLinksFor && (
        <Section className="py-6">
          <RelatedLinksRail sourcePath={props.relatedLinksFor} />
        </Section>
      )}

      {/* Invändningshantering (CRO — sänker tröskeln före closing) */}
      {props.objections && props.objections.items.length > 0 && (
        <Section className="py-10" id="invandningar">
          <div className="mx-auto max-w-4xl">
            <EyebrowHeading
              eyebrow="Tveksamheter"
              title={<>{props.objections.heading ?? "Det ni kanske undrar"}</>}
            />
            {props.objections.intro && (
              <p className="mx-auto mt-4 max-w-2xl text-pretty text-base text-foreground/80">
                {props.objections.intro}
              </p>
            )}
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {props.objections.items.map((o) => (
                <div
                  key={o.objection}
                  className="rounded-2xl border border-white/10 bg-surface/40 p-6"
                >
                  <div className="text-base font-semibold text-foreground">
                    "{o.objection}"
                  </div>
                  <div className="mt-3 text-pretty text-sm leading-relaxed text-foreground/85">
                    {o.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* Riskreducerare — garantiband som sänker tröskeln före closing-CTA */}
      {props.riskReducers && (() => {
        const cfg =
          props.riskReducers === true
            ? { heading: undefined as string | undefined, items: DEFAULT_RISK_REDUCERS }
            : props.riskReducers;
        if (!cfg.items.length) return null;
        return (
          <Section className="py-10" id="garantier">
            <EyebrowHeading
              eyebrow="Trygghet"
              title={<>{cfg.heading ?? "Varför det är tryggt att börja"}</>}
            />
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {cfg.items.map((r) => {
                const Icon = r.icon ?? ShieldCheck;
                return (
                  <article
                    key={r.title}
                    className="flex flex-col rounded-3xl border border-white/10 bg-surface p-7"
                  >
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-mint/15 text-mint ring-1 ring-mint/30">
                      <Icon className="h-5 w-5" strokeWidth={2} />
                    </div>
                    <h3 className="mt-5 text-lg font-semibold leading-tight">{r.title}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{r.body}</p>
                  </article>
                );
              })}
            </div>
          </Section>
        );
      })()}

      {/* Lead magnet — klusterspecifik nedladdning före closing-CTA */}
      {props.leadMagnet && (
        <Section className="py-10" id="guide">
          <div className="mx-auto max-w-3xl">
            <LeadMagnetForm
              location={`landing_${props.trackingId}_magnet`}
              magnetSlug={props.leadMagnet.slug}
              title={props.leadMagnet.title}
              description={props.leadMagnet.description}
              ctaLabel={props.leadMagnet.ctaLabel}
            />
          </div>
        </Section>
      )}

      {/* Closing CTA */}
      {props.closing && (
        <Section>
          <div className="rounded-3xl border border-white/10 bg-surface/60 p-8 text-center md:p-12">
            <h2 className="text-balance text-3xl font-semibold md:text-4xl">
              {props.closing.title}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
              {props.closing.body}
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Button
                to={props.closing.primary.to}
                variant="mint"
                arrow
                onClick={() => onContactCta(props.closing!.primary.to, props.closing!.primary.label, "landing_closing_cta")}
              >
                {props.closing.primary.label}

              </Button>
              {props.closing.secondary && (
                <Button to={props.closing.secondary.to} variant="outline">
                  {props.closing.secondary.label}
                </Button>
              )}
            </div>
          </div>
        </Section>
      )}

      {props.contactSlot === false ? null : props.contactSlot ? (
        props.contactSlot
      ) : (
        <>
          <CallbackBar />
          <ContactForm location={`landing_${props.trackingId}`} />
        </>
      )}
    </article>
  );
}
